'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Check } from 'lucide-react';

const personas = [
  {
    id: 'marketing',
    label: 'For Marketing Teams',
    headline: 'Your engineers already have the stories. Stop chasing them.',
    description: 'Set up Conversation Guides for your engineers and leaders. They talk about what they know. AI extracts the moments. You get authentic LinkedIn content without writing a single word.',
    useCase: 'Solar company: Engineers record conversations about installations. Marketing creates LinkedIn posts from AI-extracted moments with real technical insights. No ghostwriting. No scripts.',
    features: [
      'Employee content gets 8x more engagement than brand posts',
      "AI creates posts in the speaker's authentic voice",
      '14 moment types detected automatically',
      'Export clips ready for LinkedIn, X, Instagram',
    ],
    cta: 'Start Your Team Free',
    href: '/start',
  },
  {
    id: 'creators',
    label: 'For Consultants & Hosts',
    headline: 'Every client call is a content goldmine you\'re ignoring.',
    description: 'Record your consulting sessions and podcast interviews. AI finds the quotable moments, the frameworks, the breakthroughs.',
    useCase: 'Leadership coach: Records monthly sessions with executives. AI finds key frameworks and "aha moments." Each session generates a LinkedIn post and a private clip for the client.',
    features: [
      'Turn 1 conversation into 10+ content pieces',
      'Charge for your time with built-in Stripe payments',
      'Build a searchable library of all your sessions',
      'Your booking page at apprendo.co/@you',
    ],
    cta: 'Start Creating Free',
    href: '/start',
  },
  {
    id: 'companies',
    label: 'For Growing Companies',
    headline: 'Record once, everyone learns.',
    description: "One conversation creates searchable moments for the whole team. No more \"can you send me the recording?\" — everyone has access instantly.",
    useCase: 'Hardware company: President records weekly industry insights. Marketing pulls moments for social, sales uses clips for prospect education, HR shares onboarding content — all from the same conversation.',
    features: [
      'Shared knowledge library across departments',
      'Marketing searches for quotes, Sales finds objection handles',
      'Attribution: content → pipeline → hires',
      'ROI: time invested vs results generated',
    ],
    cta: 'Talk to Sales',
    href: 'mailto:sales@apprendo.com',
  },
];

export default function PersonaTabsSection() {
  const [activeTab, setActiveTab] = useState('marketing');
  const active = personas.find((p) => p.id === activeTab) || personas[0];

  return (
    <section className="py-20 bg-card border-t border-b border-border">
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="text-center mb-10">
          <div className="landing-section-label">Built for how your team works</div>
          <h2 className="text-[38px] font-bold tracking-[-1.5px] mb-3.5 text-foreground">
            Different teams, same platform
          </h2>
        </div>

        <div className="flex justify-center gap-2 mb-10 flex-wrap">
          {personas.map((p) => (
            <button
              key={p.id}
              onClick={() => setActiveTab(p.id)}
              className={`px-5 py-2.5 rounded-full text-[14px] font-medium transition-all ${
                activeTab === p.id
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground border border-border hover:border-foreground hover:text-foreground'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h3 className="text-[26px] font-bold tracking-[-0.5px] mb-3 text-foreground">
              {active.headline}
            </h3>
            <p className="text-[15px] text-muted-foreground leading-[1.7] mb-6">
              {active.description}
            </p>
            <ul className="space-y-2.5 mb-6">
              {active.features.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-[14px] text-foreground">
                  <Check className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Link
              href={active.href}
              className="inline-flex items-center gap-2 bg-[#EF4444] text-white px-6 py-3 rounded-full text-[14px] font-semibold hover:bg-[#DC2626] transition-all"
            >
              {active.cta}
            </Link>
          </div>
          <div className="bg-muted border border-border rounded-[16px] p-7">
            <div className="text-[11px] uppercase tracking-[1px] text-muted-foreground mb-3 font-space-mono">
              Real use case
            </div>
            <p className="text-[14px] leading-[1.7] text-foreground">{active.useCase}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
